<template>
  <div
    class="from-amber-50 to-orange-50 py-16"
    :style="{
      backgroundImage: `url(${backgroundImg})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center'
    }"
  >
    <div class="max-w-7xl mx-auto px-4">
      <div
        class="bg-white/50 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden"
      >
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <!-- 左侧视频区域 -->
          <div class="p-4 lg:p-8 xl:p-12 flex items-center justify-center">
            <div class="relative w-full video-container">
              <!-- 视频播放器容器 -->
              <div class="video-wrapper" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
                <video
                  ref="videoPlayer"
                  class="video-player"
                  :style="{ objectFit: videoFitMode }"

                  :controls="false"
                  preload="metadata"
                  @click="togglePlay"
                  @loadedmetadata="onVideoLoaded"
                  @timeupdate="onTimeUpdate"
                  @ended="onVideoEnded"
                >
                  <source :src="video" type="video/mp4" />
                  您的浏览器不支持视频播放。
                </video>
                
                <!-- 自定义播放控件覆盖层 -->
                <div class="video-overlay" :class="{ 'show-controls': showControls || !isPlaying }">
                  <!-- 中央播放按钮 -->
                  <div class="play-button-center" v-show="!isPlaying" @click="togglePlay">
                    <div class="play-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                  
                  <!-- 底部控制栏 -->
                  <div class="video-controls" v-show="showControls || !isPlaying">
                    <!-- 进度条 -->
                    <div class="progress-container" @click="seekTo">
                      <div class="progress-bar">
                        <div class="progress-filled" :style="{ width: progressPercent + '%' }"></div>
                      </div>
                    </div>
                    
                    <!-- 控制按钮 -->
                    <div class="controls-row">
                      <button class="control-btn play-pause" @click="togglePlay">
                        <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                        <svg v-else viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                        </svg>
                      </button>
                      
                      <span class="time-display">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>
                      
                      <button class="control-btn volume-btn" @click="toggleMute">
                        <svg v-if="!isMuted" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                        </svg>
                        <svg v-else viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                        </svg>
                      </button>
                      
                      <button class="control-btn fit-mode-btn" @click="toggleVideoFitMode" :title="videoFitMode === 'cover' ? '切换到适应模式' : '切换到填充模式'">
                        <svg v-if="videoFitMode === 'cover'" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5.04-6.71l-2.75 3.54-1.96-2.36L6.5 17h11l-3.54-4.71z"/>
                        </svg>
                        <svg v-else viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7h9v6h-9z"/>
                        </svg>
                      </button>
                      
                      <button class="control-btn fullscreen-btn" @click="toggleFullscreen">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧内容区域 -->
          <div class="p-6 lg:p-12 flex flex-col justify-center max-h-[500px]">
            <!-- 标题 -->
            <div class="mb-8">
              <h2
                class="text-3xl lg:text-4xl font-bold text-[#2f5c45] mb-6 italic"
              >
                {{ $t('companyIntroduction.title') }}
              </h2>

              <!-- 公司描述 -->
              <div class="text-gray-700 leading-relaxed text-xs">
                <div
                  v-for="(paragraph, index) in descriptionParagraphs"
                  :key="index"
                  class="mb-2"
                >
                  {{ paragraph }}
                </div>
              </div>
            </div>

            <!-- 认证图标 - PC端优化布局 -->
            <div class="w-full">
              <!-- PC端：使用flex布局，确保不溢出 -->
              <div class="hidden lg:flex lg:flex-wrap lg:justify-start lg:items-center lg:gap-4 xl:gap-6">
                <!-- ISO认证 -->
                <div class="text-center flex-shrink-0 w-16">
                  <div
                    class="w-16 h-16 mx-auto mb-2 bg-gray-100 rounded-lg flex items-center justify-center border"
                  >
                    <img :src="yi" class="w-12 h-12 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight">
                    {{ $t('companyIntroduction.certifications.iso') }}
                  </span>
                </div>

                <!-- 完整设备 -->
                <div class="text-center flex-shrink-0 w-16">
                  <div
                    class="w-16 h-16 mx-auto mb-2 bg-gray-100 rounded-lg flex items-center justify-center border"
                  >
                    <img :src="er" class="w-12 h-12 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight">
                    {{ $t('companyIntroduction.certifications.corporate') }}
                  </span>
                </div>

                <!-- 技术支持 -->
                <div class="text-center flex-shrink-0 w-16">
                  <div
                    class="w-16 h-16 mx-auto mb-1 bg-gray-100 rounded-lg flex items-center justify-center border"
                  >
                    <img :src="san" class="w-12 h-12 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight">
                    {{ $t('companyIntroduction.certifications.support') }}
                  </span>
                </div>

                <!-- 全球供应 -->
                <div class="text-center flex-shrink-0 w-16">
                  <div
                    class="w-16 h-16 mx-auto mb-2 bg-gray-100 rounded-lg flex items-center justify-center border"
                  >
                    <img :src="si" class="w-12 h-12 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight">
                    {{ $t('companyIntroduction.certifications.global') }}
                  </span>
                </div>

                <!-- FSC认证 -->
                <div class="text-center flex-shrink-0 w-16">
                  <div
                    class="w-16 h-16 mx-auto mb-2 bg-gray-100 rounded-lg flex items-center justify-center border"
                  >
                    <img :src="wu" class="w-12 h-12 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight">
                    {{ $t('companyIntroduction.certifications.fsc') }}
                  </span>
                </div>
              </div>

              <!-- 移动端：网格布局 -->
              <div class="grid grid-cols-3 gap-4 lg:hidden">
                <!-- ISO认证 -->
                <div class="text-center">
                  <div
                    class="w-14 h-14 mx-auto mb-2 bg-white/80 rounded-xl shadow-md border border-gray-200 flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <img :src="yi" class="w-10 h-10 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight font-medium">
                    {{ $t('companyIntroduction.certifications.iso') }}
                  </span>
                </div>

                <!-- 完整设备 -->
                <div class="text-center">
                  <div
                    class="w-14 h-14 mx-auto mb-2 bg-white/80 rounded-xl shadow-md border border-gray-200 flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <img :src="er" class="w-10 h-10 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight font-medium">
                    {{ $t('companyIntroduction.certifications.corporate') }}
                  </span>
                </div>

                <!-- 技术支持 -->
                <div class="text-center">
                  <div
                    class="w-14 h-14 mx-auto mb-2 bg-white/80 rounded-xl shadow-md border border-gray-200 flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <img :src="san" class="w-10 h-10 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight font-medium">
                    {{ $t('companyIntroduction.certifications.support') }}
                  </span>
                </div>

                <!-- 全球供应 -->
                <div class="text-center">
                  <div
                    class="w-14 h-14 mx-auto mb-2 bg-white/80 rounded-xl shadow-md border border-gray-200 flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <img :src="si" class="w-10 h-10 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight font-medium">
                    {{ $t('companyIntroduction.certifications.global') }}
                  </span>
                </div>

                <!-- FSC认证 -->
                <div class="text-center">
                  <div
                    class="w-14 h-14 mx-auto mb-2 bg-white/80 rounded-xl shadow-md border border-gray-200 flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <img :src="wu" class="w-10 h-10 object-contain" />
                  </div>
                  <span class="text-xs text-gray-600 block leading-tight font-medium">
                    {{ $t('companyIntroduction.certifications.fsc') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup name="CompanyIntroduction">
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import backgroundImg from '@/assets/other/WechatIMG3658.jpg'
  import yi from '@/assets/shipin/1.png'
  import er from '@/assets/shipin/2.png'
  import san from '@/assets/shipin/3.png'
  import si from '@/assets/shipin/4.png'
  import wu from '@/assets/shipin/5.png'
  import video from '@/assets/home/video.mp4'

  const { t } = useI18n()

  // 视频相关状态
  const videoPlayer = ref(null)
  const videoPoster = ref(
    'https://ai-public.mastergo.com/ai/img_res/79cbdeddbe75de108d3ce17b250a78c2.jpg'
  )
  const isPlaying = ref(false)
  const showControls = ref(false)
  const isMouseOver = ref(false)
  const currentTime = ref(0)
  const duration = ref(0)
  const isMuted = ref(false)
  const progressPercent = ref(0)
  const videoFitMode = ref('cover') // 'cover' 或 'contain'
  
  let controlsTimeout = null

  // 将描述文本按段落分割
  const descriptionParagraphs = computed(() => {
    return t('companyIntroduction.description')
      .split('\n')
      .filter(p => p.trim())
  })

  // 视频控制方法
  const togglePlay = () => {
    if (!videoPlayer.value) return
    
    if (isPlaying.value) {
      videoPlayer.value.pause()
    } else {
      videoPlayer.value.play()
    }
    isPlaying.value = !isPlaying.value
    showControlsTemporarily()
  }

  const toggleMute = () => {
    if (!videoPlayer.value) return
    
    videoPlayer.value.muted = !videoPlayer.value.muted
    isMuted.value = videoPlayer.value.muted
    showControlsTemporarily()
  }

  const toggleFullscreen = () => {
    if (!videoPlayer.value) return
    
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      videoPlayer.value.requestFullscreen()
    }
    showControlsTemporarily()
  }

  const seekTo = (event) => {
    if (!videoPlayer.value || !duration.value) return
    
    const rect = event.currentTarget.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const percentage = clickX / rect.width
    const newTime = percentage * duration.value
    
    videoPlayer.value.currentTime = newTime
    currentTime.value = newTime
    progressPercent.value = percentage * 100
    showControlsTemporarily()
  }

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00'
    
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const showControlsTemporarily = () => {
    showControls.value = true
    if (controlsTimeout) {
      clearTimeout(controlsTimeout)
    }
    controlsTimeout = setTimeout(() => {
      if (isPlaying.value && !isMouseOver.value) {
        showControls.value = false
      }
    }, 100)
  }

  // 鼠标事件处理
  const onMouseEnter = () => {
    isMouseOver.value = true
    if (isPlaying.value) {
      showControls.value = true
    }
  }

  const onMouseLeave = () => {
    isMouseOver.value = false
    if (isPlaying.value) {
      showControlsTemporarily()
    }
  }

  // 切换视频显示模式
  const toggleVideoFitMode = () => {
    videoFitMode.value = videoFitMode.value === 'cover' ? 'contain' : 'cover'
  }

  // 视频事件处理
  const onVideoLoaded = () => {
    if (videoPlayer.value) {
      duration.value = videoPlayer.value.duration
    }
  }

  const onTimeUpdate = () => {
    if (videoPlayer.value && duration.value) {
      currentTime.value = videoPlayer.value.currentTime
      progressPercent.value = (currentTime.value / duration.value) * 100
    }
  }

  const onVideoEnded = () => {
    isPlaying.value = false
    showControls.value = true
    progressPercent.value = 0
    currentTime.value = 0
  }

  onMounted(() => {
    // 组件挂载时的初始化逻辑
  })
  
  // 清理函数
  onUnmounted(() => {
    if (controlsTimeout) {
      clearTimeout(controlsTimeout)
    }
  })
</script>

<style scoped>
  /* 视频播放器样式 */
  .video-container {
    max-width: 100%;
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9; /* 固定16:9比例 */
    min-height: 300px; /* PC端最小高度 */
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.9) 100%);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  /* PC端视频高度优化 */
  @media (min-width: 1025px) {
    .video-wrapper {
      min-height: 400px; /* PC端增加最小高度 */
      max-height: 500px; /* 限制最大高度避免过高 */
    }
  }

  /* 超大屏幕进一步优化 */
  @media (min-width: 1280px) {
    .video-wrapper {
      min-height: 450px;
      max-height: 550px;
    }
  }

  .video-player {
    width: 100%;
    height: 100%;
    cursor: pointer;
    transition: transform 0.3s ease, object-fit 0.3s ease;
  }

  .video-player:hover {
    transform: scale(1.02);
  }

  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(transparent 60%, rgba(0, 0, 0, 0.7));
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    pointer-events: none;
  }

  .video-overlay.show-controls {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  .play-button-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.8);
    pointer-events: auto;
    cursor: pointer;
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .video-overlay.show-controls .play-button-center {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  .play-icon {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #2f5c45;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .play-icon:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }

  .play-icon svg {
    width: 24px;
    height: 24px;
    margin-left: 3px;
  }

  .video-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    pointer-events: auto;
    opacity: 0;
    transform: translateY(100%);
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    background: rgba(0, 0, 0, 0.1);
  }

  .video-overlay.show-controls .video-controls {
    opacity: 1;
    transform: translateY(0);
  }

  .progress-container {
    margin-bottom: 12px;
    cursor: pointer;
    transition: transform 0.2s ease-in-out;
  }

  .progress-container:hover {
    transform: scaleY(1.2);
  }

  .progress-bar {
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    overflow: hidden;
    transition: height 0.2s ease-in-out;
  }

  .progress-container:hover .progress-bar {
    height: 6px;
  }

  .progress-filled {
    height: 100%;
    background: #2f5c45;
    transition: width 0.1s ease, background-color 0.2s ease-in-out;
  }

  .progress-container:hover .progress-filled {
    background: #3d7a56;
  }

  .controls-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .control-btn {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.3s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: scale(1);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .control-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  .control-btn svg {
    width: 20px;
    height: 20px;
  }

  .time-display {
    color: white;
    font-size: 14px;
    font-weight: 500;
    margin-left: auto;
    margin-right: 12px;
  }

  /* 自定义样式 */
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  /* PC端布局优化 - 保持原有样式 */
  @media (min-width: 1025px) {
    /* 确保PC端图标不溢出 */
    .lg\:flex-wrap {
      flex-wrap: wrap;
    }
    
    .lg\:justify-start {
      justify-content: flex-start;
    }
    
    /* PC端字体大小保持原有 */
    .text-3xl {
      font-size: 1.875rem;
      line-height: 2.25rem;
    }
    
    .text-4xl {
      font-size: 2.25rem;
      line-height: 2.5rem;
    }
    
    .text-xs {
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }

  /* 超大屏幕优化 */
  @media (min-width: 1280px) {
    .xl\:gap-6 {
      gap: 1.5rem;
    }
  }

  /* 平板端适配 */
  @media (min-width: 769px) and (max-width: 1024px) {
    .py-16 {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    .text-3xl {
      font-size: 1.5rem !important;
      line-height: 2rem !important;
    }
    
    .text-4xl {
      font-size: 1.75rem !important;
      line-height: 2.25rem !important;
    }
    
    .min-h-\[500px\] {
      min-height: 400px;
    }
    
    .p-6 {
      padding: 1.25rem;
    }
    
    .p-8 {
      padding: 1.5rem;
    }
  }

  /* 移动端响应式优化 */
  @media (max-width: 768px) {
    .py-16 {
      padding-top: 2rem;
      padding-bottom: 2rem;
    }
    
    .px-4 {
      padding-left: 1rem;
      padding-right: 1rem;
    }
    
    .p-6 {
      padding: 1rem;
    }
    
    .p-8 {
      padding: 1rem;
    }
    
    .min-h-\[500px\] {
      min-height: 300px;
    }
    
    .mb-8 {
      margin-bottom: 1.5rem;
    }
    
    .mb-6 {
      margin-bottom: 1rem;
    }
    
    /* 移动端标题字体优化 */
    .text-3xl {
      font-size: 1.25rem !important;
      line-height: 1.75rem !important;
    }
    
    .text-4xl {
      font-size: 1.5rem !important;
      line-height: 2rem !important;
    }
    
    /* 移动端描述文字优化 */
    .text-xs {
      font-size: 0.8rem !important;
      line-height: 1.25rem !important;
    }
    
    .grid-cols-3 {
      grid-template-columns: repeat(3, 1fr);
      justify-items: center;
      gap: 0.75rem;
    }
    
    /* 移动端认证图标文字优化 */
    .grid .text-xs {
      font-size: 0.7rem !important;
      line-height: 1rem !important;
    }
    
    /* 移动端视频播放器优化 */
    .video-wrapper {
      border-radius: 8px;
    }
    
    .play-icon {
      width: 60px;
      height: 60px;
    }
    
    .play-icon svg {
      width: 24px;
      height: 24px;
    }
    
    .video-controls {
      padding: 12px;
    }
    
    .controls-row {
      gap: 8px;
    }
    
    .control-btn {
      padding: 6px;
    }
    
    .control-btn svg {
      width: 18px;
      height: 18px;
    }
    
    .time-display {
      font-size: 12px;
      margin-right: 8px;
    }
    
    .progress-bar {
      height: 3px;
    }
    
    .video-player:hover {
      transform: none; /* 移动端禁用hover缩放 */
    }
  }

  /* 小屏手机优化 */
  @media (max-width: 480px) {
    .py-16 {
      padding-top: 1.5rem;
      padding-bottom: 1.5rem;
    }
    
    .px-4 {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
    
    .p-6 {
      padding: 0.75rem;
    }
    
    .min-h-\[500px\] {
      min-height: 250px;
    }
    
    /* 小屏手机标题字体进一步优化 */
    .text-3xl {
      font-size: 1.125rem !important;
      line-height: 1.5rem !important;
    }
    
    .text-4xl {
      font-size: 1.25rem !important;
      line-height: 1.75rem !important;
    }
    
    .text-xs {
      font-size: 0.75rem !important;
      line-height: 1.125rem !important;
    }
    
    .grid .text-xs {
      font-size: 0.65rem !important;
      line-height: 0.9rem !important;
    }
    
    .grid-cols-3 {
      gap: 0.5rem;
    }
    
    .w-14 {
      width: 3rem;
      height: 3rem;
    }
    
    .h-14 {
      width: 3rem;
      height: 3rem;
    }
    
    .w-10 {
      width: 2.25rem;
      height: 2.25rem;
    }
    
    .h-10 {
      width: 2.25rem;
      height: 2.25rem;
    }
    
    /* 小屏手机视频播放器优化 */
    .play-icon {
      width: 50px;
      height: 50px;
    }
    
    .play-icon svg {
      width: 20px;
      height: 20px;
    }
    
    .video-controls {
      padding: 8px;
    }
    
    .controls-row {
      gap: 6px;
    }
    
    .control-btn {
      padding: 4px;
    }
    
    .control-btn svg {
      width: 16px;
      height: 16px;
    }
    
    .time-display {
      font-size: 11px;
      margin-right: 6px;
    }
  }

  /* 超小屏优化 */
  @media (max-width: 360px) {
    .text-3xl {
      font-size: 1rem !important;
      line-height: 1.375rem !important;
    }
    
    .text-4xl {
      font-size: 1.125rem !important;
      line-height: 1.5rem !important;
    }
    
    .grid .text-xs {
      font-size: 0.6rem !important;
      line-height: 0.8rem !important;
    }
  }

  /* 触摸设备优化 */
  @media (hover: none) and (pointer: coarse) {
    .hover\:scale-105:hover {
      transform: none;
    }
    
    .hover\:shadow-lg:hover {
      box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    }
    
    /* 触摸时的反馈效果 */
    .transition-all:active {
      transform: scale(0.95);
    }
    
    /* 触摸设备视频播放器优化 */
    .video-player:hover {
      transform: none;
    }
    
    .play-icon:hover {
      transform: none;
      background: rgba(255, 255, 255, 0.9);
    }
    
    .play-icon:active {
      transform: scale(0.95);
      background: rgba(255, 255, 255, 1);
    }
    
    .control-btn:hover {
      background: none;
    }
    
    .control-btn:active {
      background: rgba(255, 255, 255, 0.3);
    }
    
    /* 触摸设备上始终显示控件 */
    .video-overlay {
      opacity: 1;
      pointer-events: auto;
    }
  }

  /* 横屏模式优化 */
  @media (max-width: 768px) and (orientation: landscape) {
    .py-16 {
      padding-top: 1rem;
      padding-bottom: 1rem;
    }
    
    .min-h-\[500px\] {
      min-height: 200px;
    }
    
    .mb-8 {
      margin-bottom: 1rem;
    }
    
    .mb-6 {
      margin-bottom: 0.75rem;
    }
    
    /* 横屏模式视频播放器优化 */
    .video-wrapper {
      aspect-ratio: 16/9;
      max-height: 60vh;
    }
    
    .play-icon {
      width: 50px;
      height: 50px;
    }
    
    .play-icon svg {
      width: 20px;
      height: 20px;
    }
    
    .video-controls {
      padding: 8px;
    }
    
    .time-display {
      font-size: 11px;
    }
  }

  /* 确保可访问性 */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>
